import React from 'react';
import { clsx } from 'clsx';
import { type CustomContentProps, SnackbarContent, useSnackbar } from 'notistack';

import { ErrorIcon, InfoIcon, SuccessIcon } from 'ui/icons';

import classes from './Notification.module.scss';

const iconsMap = {
  default: <InfoIcon />,
  error: <ErrorIcon />,
  success: <SuccessIcon />,
  warning: <ErrorIcon />,
  info: <InfoIcon />,
};

const NotificationInternal: React.ForwardRefRenderFunction<
  HTMLDivElement,
  CustomContentProps & { href?: string }
> = ({ id, iconVariant, variant, action, href, ...props }, ref) => {
  const { closeSnackbar } = useSnackbar();

  const handleClick = () => {
    closeSnackbar(id);
    if (typeof href === 'string' && href.startsWith('/')) {
      window.dispatchEvent(new CustomEvent('myteacher:navigate', { detail: { href } }));
    }
  };

  return (
    <SnackbarContent
      ref={ref}
      className={classes.snackbarContent}
      onClick={handleClick}
      role='button'
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleClick();
        }
      }}
    >
      <div className={classes.body}>
        <div className={clsx(classes.paper, classes[`paper--${variant}`])}>
          <div className={classes.glassEffect}></div>
          <div className={classes.glassTint}></div>
          <div className={classes.glassShine}></div>
          <div
            className={clsx(classes.glassPalette, { [classes[variant]]: variant })}
          ></div>
          <div className={classes.content}>
            <div className={classes.info}>
              <div className={clsx(classes.iconContainer, { [classes[variant]]: variant })}>
                {iconsMap[variant]}
              </div>
              <div className={classes.text}>{props.message}</div>
            </div>
          </div>
        </div>
      </div>
      <svg style={{ display: 'none' }} aria-hidden='true'>
        <filter
          id='glass-distortion'
          x='0%'
          y='0%'
          width='100%'
          height='100%'
          filterUnits='objectBoundingBox'
        >
          <feTurbulence
            type='fractalNoise'
            baseFrequency='0.02 0.03'
            numOctaves='3'
            seed='42'
            result='turbulence'
          />
          <feComponentTransfer in='turbulence' result='mapped'>
            <feFuncR type='gamma' amplitude='1' exponent='1' offset='0.5' />
            <feFuncG type='gamma' amplitude='0' exponent='1' offset='0' />
            <feFuncB type='gamma' amplitude='0' exponent='1' offset='0.5' />
          </feComponentTransfer>

          <feGaussianBlur in='turbulence' stdDeviation='30' result='softMap' />

          <feSpecularLighting
            in='softMap'
            surfaceScale='5'
            specularConstant='1'
            specularExponent='100'
            lightingColor='white'
            result='specLight'
          >
            <fePointLight x='-200' y='-200' z='300' />
          </feSpecularLighting>

          <feComposite
            in='specLight'
            operator='arithmetic'
            k1='0'
            k2='1'
            k3='1'
            k4='0'
            result='litImage'
          />

          <feDisplacementMap
            in='SourceGraphic'
            in2='softMap'
            scale='150'
            xChannelSelector='R'
            yChannelSelector='G'
          />
        </filter>
      </svg>
    </SnackbarContent>
  );
};

export const Notification = React.forwardRef(NotificationInternal);
